import { useHybridData } from "./useHybridData";
import { supabase } from "@/integrations/supabase/client";
import type { Doctor } from "@/types/schema";
import doctorsIndex from "@/data/generated/doctors-index.json";
import { useMemo } from "react";

// Load all individual doctor files for static detail views
const doctorDetails = import.meta.glob('@/data/generated/doctors/*.json', { eager: true });

// Helper to look up static detail by slug
const getStaticDoctor = (slug: string): Doctor | null => {
  const match = Object.entries(doctorDetails).find(([path]) => path.includes(`/${slug}.json`));
  return match ? (match[1] as { default: Doctor }).default : null;
};

// Fetch only NEW/UPDATED doctors since build
const fetchNewDoctors = async (lastBuildDate: string) => {
  const { data, error } = await supabase
    .from("doctors")
    .select("*")
    .gt("updated_at", lastBuildDate) // Use updated_at to catch edits too
    .order("is_featured", { ascending: false });

  if (error) {
    console.error("Error fetching new/updated doctors:", error);
    return null;
  }
  return data as Doctor[];
};

export function useDoctors() {
  const { data, isStatic, isCheckingUpdates } = useHybridData<Doctor>({
    resourceName: "Doctors",
    staticData: doctorsIndex as unknown as Doctor[],
    fetchDynamic: fetchNewDoctors,
    queryKey: ["doctors"],
    sortFn: (a, b) => {
      if (a.is_featured !== b.is_featured) return a.is_featured ? -1 : 1;
      return (b.rating || 0) - (a.rating || 0);
    },
    skipUpdates: true,
  });

  return {
    data,
    isLoading: false, // Always have static data instantly
    isStatic,
    isCheckingUpdates
  };
}

export function useDoctorBySlug(slug: string | undefined) {
  // 1. Try to find in static detailed files
  const staticDetail = useMemo(() => 
    slug ? getStaticDoctor(slug) : null, [slug]
  );

  // 2. Fallback: Index
  const { data: allDoctors } = useDoctors();
  const foundInList = useMemo(() => {
    return allDoctors?.find(d => d.slug === slug);
  }, [allDoctors, slug]);

  const initialData = staticDetail || foundInList;

  const { data: fullData, isCheckingUpdates: isLoading } = useHybridData<Doctor>({
    resourceName: `Doctor: ${slug}`,
    staticData: initialData ? [initialData] : [],
    fetchDynamic: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from("doctors")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();
      
      if (error) {
        console.error(`Error fetching doctor ${slug}:`, error);
        return null;
      }
      return data ? [data as Doctor] : [];
    },
    queryKey: ["doctor", slug || ""],
    skipUpdates: true,
  });

  const doctor = fullData?.[0] || initialData;

  return {
    data: doctor,
    isLoading: !doctor && isLoading,
  };
}

export function useFeaturedDoctors(limit: number = 6) {
  const { data } = useDoctors();
  
  const featured = useMemo(() => {
    return data
      .filter(d => d.is_featured)
      .slice(0, limit);
  }, [data, limit]);

  return {
    data: featured,
    isLoading: false
  };
}
